// Phase 15 M6 — "Miñas compras" — buyer-side sales tracker.
//
// Wraps `GET /api/v1/marketplace/sales/me/buyer` (returns
// MarketplaceSaleListResponse — summary cards only, no bids/timeline). For
// each sale the page renders the headline status + the contextual CTA the
// PRD spelled out:
//
//   CONTRACT_PENDING (buyer)   → "Asinar agora" (redir to /checkout?payment=ok)
//   CONFIRMED / PAYMENT_HELD   → "Descargar contrato" (cando hai contract_url)
//                                ou "Agardando contrato sinado" se aínda non chegou
//   PAYMENT_HELD               → "Pagamento en escrow"
//   IN_TRANSIT                 → "En transporte"
//   DELIVERED                  → "Confirmar recibido" + "Abrir disputa"
//   COMPLETED                  → "Compra finalizada"
//
// We can't tell from the *summary* schema alone whether the contract has
// been signed by the buyer (the `contract_signed_at` field lives on the
// detail). The "Asinar agora" CTA therefore always sends the user to
// CheckoutPage in its post-payment branch (`?payment=ok`) where the
// detail fetch + signing state machine from M5 handles the rest.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { MarketplaceSaleStatus, MarketplaceSaleSummary } from '@leyrochain/shared';

import { describeAxiosError } from '../api/errors';
import { markDelivered, salesMineBuyer } from '../api/marketplace';

const STATUS_LABEL: Record<MarketplaceSaleStatus, string> = {
  LISTED: 'En venda',
  OFFER_PLACED: 'Oferta recibida',
  AUCTION_WON: 'Subasta ganada — pendente de pagar',
  RESERVED: 'Reservado',
  PAYMENT_HELD: 'Pagamento en escrow',
  CONTRACT_PENDING: 'Contrato pendente',
  CONFIRMED: 'Confirmada',
  IN_TRANSIT: 'En transporte',
  DELIVERED: 'Entregado',
  COMPLETED: 'Finalizada',
  CANCELLED: 'Cancelada',
  DISPUTED: 'En disputa',
  RESOLVED: 'Resolta',
};

function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

export default function MyPurchasesPage(): JSX.Element {
  const navigate = useNavigate();
  const [sales, setSales] = useState<MarketplaceSaleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await salesMineBuyer();
      setSales(data.results);
      setError(null);
    } catch {
      setError('Non se puido cargar a túa lista de compras. Téntao máis tarde.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onConfirmDelivered = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        await markDelivered(id);
        await load();
      } catch (err) {
        setError(describeAxiosError(err, 'Non se puido confirmar a recepción.'));
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  // Phase 27 (B-13) — Open dispute now navigates to a dedicated page
  // with categorisation + textarea + counter + guard rails. The native
  // window.prompt() flow was UX poor (no validation, looked like a
  // phishing artefact in some browsers, no context).
  const onOpenDispute = useCallback(
    (id: string) => {
      navigate(`/marketplace/sales/${id}/dispute`);
    },
    [navigate],
  );

  if (sales === null && !error) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="h-24 rounded-xl bg-border/40 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-extrabold text-rauxa">Miñas compras</h1>
      <p className="text-sm text-muted mt-1">Estado das túas reservas e compras no marketplace.</p>

      {error ? (
        <div
          role="alert"
          className="mt-4 p-3 rounded-lg bg-red-50 border border-danger text-danger text-sm"
        >
          {error}
        </div>
      ) : null}

      {sales && sales.length === 0 ? (
        <div className="mt-8 p-8 text-center bg-white border border-border rounded-xl">
          <p className="text-muted">Aínda non mercaches nada.</p>
          <Link
            to="/marketplace"
            className="inline-block mt-3 px-4 py-2 rounded-lg bg-[#2D6E1A] text-white font-semibold"
          >
            Visita o marketplace
          </Link>
        </div>
      ) : null}

      <ul className="mt-6 space-y-3">
        {(sales ?? []).map((sale) => (
          <li
            key={sale.id}
            className="bg-white rounded-xl border border-border p-4 flex gap-4 items-center"
          >
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-border/40 shrink-0 flex items-center justify-center text-3xl">
              {sale.animal.photo_url ? (
                <img
                  src={sale.animal.photo_url}
                  alt={sale.animal.name ?? sale.animal.crotal_id}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>🐄</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-2">
                <p className="font-semibold text-dark truncate">
                  {sale.animal.name ?? sale.animal.crotal_id}
                </p>
                <span className="text-[11px] font-bold text-chain bg-chain-soft px-2 py-0.5 rounded-full">
                  {STATUS_LABEL[sale.status]}
                </span>
              </div>
              <p className="text-xs text-muted font-mono mt-1 truncate">
                {[sale.animal.crotal_id, sale.animal.breed].filter(Boolean).join(' · ')}
              </p>
              <p className="text-sm text-muted mt-1">
                {formatPrice(sale.final_price_cents ?? sale.price_cents)} ·{' '}
                {sale.seller.farm_name ?? sale.seller.name}
              </p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <SaleCta
                sale={sale}
                busy={busyId === sale.id}
                onConfirmDelivered={() => onConfirmDelivered(sale.id)}
                onOpenDispute={() => onOpenDispute(sale.id)}
              />
              {/* Phase 27 (B-10 fix) — buyer needs the SITRAN guide + DIB
                  PDFs to register the animal in their own farm. Backend
                  presigns the URLs on /me/buyer so these links work
                  directly. */}
              {sale.dib_pdf_url || sale.sitran_pdf_url ? (
                <div className="flex flex-col gap-1 mt-1">
                  {sale.dib_pdf_url ? (
                    <a
                      href={sale.dib_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-rauxa hover:underline whitespace-nowrap"
                    >
                      📄 DIB
                    </a>
                  ) : null}
                  {sale.sitran_pdf_url ? (
                    <a
                      href={sale.sitran_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-rauxa hover:underline whitespace-nowrap"
                    >
                      📄 Guía SITRAN
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SaleCtaProps {
  sale: MarketplaceSaleSummary;
  busy: boolean;
  onConfirmDelivered: () => void;
  onOpenDispute: () => void;
}

function SaleCta({ sale, busy, onConfirmDelivered, onOpenDispute }: SaleCtaProps): JSX.Element {
  switch (sale.status) {
    case 'CONTRACT_PENDING':
      return (
        <Link
          to={`/marketplace/checkout/${sale.id}?payment=ok`}
          className="text-sm px-3 py-1.5 rounded-lg bg-[#2D6E1A] text-white font-semibold"
        >
          Asinar agora
        </Link>
      );
    case 'CONFIRMED':
    case 'PAYMENT_HELD':
      // Lote V-3 followup — para sales CONFIRMED ofrecemos o contrato
      // sinado. Sen contract_url (legacy / aínda en proceso) volvemos a
      // mensaxe de espera para non mostrar un botón roto.
      return sale.contract_url ? (
        <a
          href={sale.contract_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm px-3 py-1.5 rounded-lg border border-rauxa text-rauxa font-semibold hover:bg-rauxa/5"
        >
          Descargar contrato
        </a>
      ) : (
        <span className="text-xs text-muted whitespace-nowrap">Agardando contrato sinado</span>
      );
    case 'IN_TRANSIT':
      // Phase 27 (B-07 fix) — the buyer is always the one who knows the
      // animal physically arrived (whether they did the pickup or the
      // seller delivered). Surface the "Confirmar recibido" CTA here too
      // — the backend transitions IN_TRANSIT → DELIVERED on this call.
      return (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirmDelivered}
            className="text-sm px-3 py-1.5 rounded-lg bg-[#2D6E1A] text-white font-semibold disabled:opacity-60"
          >
            Confirmar recibido
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onOpenDispute}
            className="text-sm px-3 py-1.5 rounded-lg border border-danger text-danger font-semibold disabled:opacity-60"
          >
            Abrir disputa
          </button>
        </>
      );
    case 'DELIVERED':
      // Phase 27 (B-07 followup) — once the buyer confirms receipt the
      // sale lands on DELIVERED and waits out a 72h dispute window
      // before auto-COMPLETED + capture. We must NOT re-offer
      // "Confirmar recibido" here (backend 409s on duplicate transition)
      // — only the dispute escape hatch remains until the window closes.
      return (
        <>
          <span className="text-xs text-chain font-semibold whitespace-nowrap">
            ✓ Recibido (xanela disputa 72h)
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={onOpenDispute}
            className="text-sm px-3 py-1.5 rounded-lg border border-danger text-danger font-semibold disabled:opacity-60"
          >
            Abrir disputa
          </button>
        </>
      );
    case 'COMPLETED':
      return <span className="text-xs text-chain font-semibold">Compra finalizada ✓</span>;
    case 'DISPUTED':
      return <span className="text-xs text-danger font-semibold">En disputa</span>;
    case 'CANCELLED':
      return <span className="text-xs text-muted">Cancelada</span>;
    default:
      return (
        <Link
          to={`/marketplace/animal/${sale.id}`}
          className="text-sm px-3 py-1.5 rounded-lg border border-border text-dark"
        >
          Ver detalle
        </Link>
      );
  }
}
